import { useCallback, useEffect, useRef, useState } from 'react';
import { Download, FileText, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { ensureDir } from '@/OS/utils/filesystem';
import { globalVFS } from '@/OS/utils/storage/VFS';
import type { UserMessageAttachment } from '@/hooks/useUserMessages';
import { cn } from '@/lib/utils';

interface MessageAttachmentsProps {
  attachments?: UserMessageAttachment[] | null;
  isOutgoing: boolean;
  onOpenWindow?: (appId: string, title: string, filePath?: string, content?: string) => void;
}

type SavedAttachment = {
  path: string;
  content: string;
};

const imageExtensions = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico']);
const textExtensions = new Set([
  'txt',
  'md',
  'csv',
  'json',
  'js',
  'ts',
  'tsx',
  'css',
  'html',
  'xml',
  'yml',
  'yaml',
  'ini',
  'log',
  'conf',
  'sys',
]);

const getExtension = (fileName: string) => fileName.split('.').pop()?.toLowerCase() || '';
const getBaseName = (fileName: string) => fileName.replace(/\.[^.]+$/, '');
const sanitizeFileName = (name: string) => name.replace(/[<>:"/\\|?*]/g, '-').trim() || 'attachment';

const blobToDataUrl = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('Failed to read attachment')); 
    reader.readAsDataURL(blob);
  });

const isImageAttachment = (attachment: UserMessageAttachment) => {
  if (attachment.kind === 'image') {
    return true;
  }

  const extension = getExtension(attachment.name);
  return attachment.mime_type?.startsWith('image/') || imageExtensions.has(extension);
};

const isTextAttachment = (attachment: UserMessageAttachment) => {
  const extension = getExtension(attachment.name);
  return (
    attachment.mime_type?.startsWith('text/') ||
    attachment.mime_type === 'application/json' ||
    attachment.mime_type === 'application/xml' ||
    textExtensions.has(extension)
  );
};

const getUserSavedFolder = (email?: string | null) => {
  const username = (email?.split('@')[0] || 'User').replace(/[^a-zA-Z0-9]/g, '') || 'User';
  return `C:\\User\\${username}\\Saved`;
};

export default function MessageAttachments({ attachments, isOutgoing, onOpenWindow }: MessageAttachmentsProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [savedAttachments, setSavedAttachments] = useState<Record<string, SavedAttachment>>({});
  const [busyIds, setBusyIds] = useState<Record<string, boolean>>({});
  const inFlightRef = useRef<Record<string, Promise<SavedAttachment>>>({});

  const ensureUniquePath = useCallback(async (folderPath: string, originalName: string) => {
    const safeName = sanitizeFileName(originalName);
    const extension = getExtension(safeName);
    const baseName = extension ? getBaseName(safeName) : safeName;

    let candidate = `${folderPath}\\${safeName}`;
    let index = 2;

    while (await globalVFS.exists(candidate)) {
      const nextName = extension ? `${baseName} (${index}).${extension}` : `${baseName} (${index})`;
      candidate = `${folderPath}\\${nextName}`;
      index += 1;
    }

    return candidate;
  }, []);

  const ensureAttachmentSaved = useCallback(
    async (attachment: UserMessageAttachment) => {
      if (savedAttachments[attachment.id]) {
        return savedAttachments[attachment.id];
      }

      if (inFlightRef.current[attachment.id]) {
        return inFlightRef.current[attachment.id];
      }

      if (!user) {
        throw new Error('You must be signed in to open attachments.');
      }

      const task = (async () => {
        setBusyIds((current) => ({ ...current, [attachment.id]: true }));

        const savedFolderPath = getUserSavedFolder(user.email);
        await ensureDir(savedFolderPath);

        const existingPath = `${savedFolderPath}\\${sanitizeFileName(attachment.name)}`;
        if (await globalVFS.exists(existingPath)) {
          const existingContent = await globalVFS.load(existingPath);
          if (existingContent !== null) {
            const existingSavedAttachment = { path: existingPath, content: existingContent };
            setSavedAttachments((current) => ({ ...current, [attachment.id]: existingSavedAttachment }));
            return existingSavedAttachment;
          }
        }

        const response = await fetch(attachment.url);
        if (!response.ok) {
          throw new Error(`Failed to download ${attachment.name}`);
        }

        const content = isTextAttachment(attachment)
          ? await response.text()
          : await blobToDataUrl(await response.blob());

        const path = await ensureUniquePath(savedFolderPath, attachment.name);
        await globalVFS.save(path, content);

        const savedAttachment = { path, content };
        setSavedAttachments((current) => ({ ...current, [attachment.id]: savedAttachment }));
        return savedAttachment;
      })();

      inFlightRef.current[attachment.id] = task;

      try {
        return await task;
      } finally {
        setBusyIds((current) => {
          const next = { ...current };
          delete next[attachment.id];
          return next;
        });
        delete inFlightRef.current[attachment.id];
      }
    },
    [ensureUniquePath, savedAttachments, user]
  );

  useEffect(() => {
    if (!attachments || attachments.length === 0 || !user) {
      return;
    }

    attachments.forEach((attachment) => {
      if (!savedAttachments[attachment.id] && !inFlightRef.current[attachment.id]) {
        void ensureAttachmentSaved(attachment).catch((error) => {
          console.error('[Messenger] Failed to save attachment locally:', error);
        });
      }
    });
  }, [attachments, ensureAttachmentSaved, savedAttachments, user]);

  const handleOpenAttachment = useCallback(
    async (attachment: UserMessageAttachment) => {
      if (!onOpenWindow) {
        toast({
          title: 'Unable to open attachment',
          description: 'MapleOS could not open a viewer window right now.',
          variant: 'destructive',
        });
        return;
      }

      try {
        const savedAttachment = await ensureAttachmentSaved(attachment);
        const fileName = savedAttachment.path.split('\\').pop() || attachment.name;

        if (isImageAttachment(attachment)) {
          onOpenWindow('image-viewer', `Image Viewer - ${fileName}`, savedAttachment.path, savedAttachment.content);
          return;
        }

        onOpenWindow('notepad', `Notepad - ${fileName}`, savedAttachment.path, savedAttachment.content);
      } catch (error: any) {
        toast({
          title: 'Attachment failed',
          description: error?.message || 'This attachment could not be saved into MapleOS.',
          variant: 'destructive',
        });
      }
    },
    [ensureAttachmentSaved, onOpenWindow, toast]
  );

  if (!attachments || attachments.length === 0) {
    return null;
  }

  const imageAttachments = attachments.filter((attachment) => attachment.kind === 'image');
  const fileAttachments = attachments.filter((attachment) => attachment.kind === 'file');

  return (
    <div className="space-y-2">
      {imageAttachments.length > 0 && (
        <div className="grid gap-2 sm:grid-cols-2">
          {imageAttachments.map((attachment) => {
            const isBusy = Boolean(busyIds[attachment.id]);
            return (
              <button
                key={attachment.id}
                type="button"
                onClick={() => void handleOpenAttachment(attachment)}
                disabled={isBusy}
                className="block overflow-hidden rounded-xl border border-border/60 bg-background/80 text-left transition hover:bg-muted/40 disabled:cursor-wait"
              >
                <img src={attachment.url} alt={attachment.name} className="h-auto max-h-72 w-full object-cover" />
                <div className="flex items-center justify-between gap-2 px-3 py-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">{attachment.name}</div>
                    <div className="text-xs text-muted-foreground">Open in Image Viewer</div>
                  </div>
                  {isBusy ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : <Download className="h-4 w-4 text-muted-foreground" />}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {fileAttachments.length > 0 && (
        <div className="space-y-2">
          {fileAttachments.map((attachment) => {
            const isBusy = Boolean(busyIds[attachment.id]);
            return (
              <div
                key={attachment.id}
                className={cn(
                  'flex items-center justify-between gap-3 rounded-xl border px-3 py-2',
                  isOutgoing ? 'border-border/60 bg-background/70' : 'border-primary/35 bg-background/80'
                )}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <div className="rounded-lg bg-muted p-2 text-muted-foreground">
                    <FileText className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">{attachment.name}</div>
                    <div className="text-xs text-muted-foreground">{attachment.mime_type || 'File attachment'}</div>
                  </div>
                </div>

                <Button
                  size="sm"
                  variant="outline"
                  className="shrink-0"
                  onClick={() => void handleOpenAttachment(attachment)}
                  disabled={isBusy}
                >
                  {isBusy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1.5 h-3.5 w-3.5" />}
                  Open
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
